import { Course } from '../types/course.type';
import { Lesson, LessonAssignment } from '../types/lesson.type';
import { instructorRolesMapping } from './consts';

export type LessonInstructorDisplay = {
  id: number;
  name: string;
  role: string;
};

/**
 * A main instructor's sub request is filled when a lesson_assignment exists for this
 * lesson with requesting_instructor_id set and a substitute instructor_id assigned.
 * Mirrors buildInstructedIdWhere / filledSubExclusionLiteral on the API.
 */
export function hasFilledSubRequest(
  lessonAssignments: LessonAssignment[],
  mainInstructorId: number
): boolean {
  const mainId = Number(mainInstructorId);
  return lessonAssignments.some(
    (la) =>
      la.requesting_instructor_id != null &&
      Number(la.requesting_instructor_id) === mainId &&
      la.instructor_id != null
  );
}

/** Sub requested but no substitute assigned yet. */
export function hasPendingSubRequest(
  lessonAssignments: LessonAssignment[],
  instructorId: number
): boolean {
  const id = Number(instructorId);
  return lessonAssignments.some(
    (la) =>
      la.requesting_instructor_id != null &&
      Number(la.requesting_instructor_id) === id &&
      la.instructor_id == null
  );
}

function formatInstructorName(
  instructor?: { full_name?: string; first_name?: string; last_name?: string } | null
): string {
  if (!instructor) return '';
  if (instructor.full_name) return instructor.full_name;
  return `${instructor.first_name ?? ''} ${instructor.last_name ?? ''}`.trim();
}

/**
 * Instructors to show on a class row/card: mains without a filled sub for this lesson,
 * plus per-lesson assignments (substitute, additional, trainee, observer).
 */
export function getLessonInstructorDisplay(
  lesson: Pick<Lesson, 'lesson_assignments'>,
  course: Pick<Course, 'course_assignments'>
): LessonInstructorDisplay[] {
  const mainInstructors = (course.course_assignments ?? [])
    .filter(
      (ca) =>
        ca.instructor_id != null &&
        !hasFilledSubRequest(lesson.lesson_assignments, ca.instructor_id)
    )
    .map((ca) => ({
      id: Number(ca.instructor_id),
      name: formatInstructorName(ca.instructor),
      role: 'Main instructor'
    }));

  const lessonInstructors = lesson.lesson_assignments
    .filter((la) => la.instructor_id != null && la.instructor)
    .map((la) => ({
      id: Number(la.instructor_id),
      name: formatInstructorName(la.instructor),
      role: instructorRolesMapping[la.instructor_role] ?? la.instructor_role
    }));

  return [...mainInstructors, ...lessonInstructors];
}

/** CSS swatch class for instructor legend styling (classes list, etc.). */
export function lessonInstructorDisplaySwatchClass(
  item: LessonInstructorDisplay,
  lessonAssignments: LessonAssignment[]
): string {
  let roleClass: string;
  if (item.role === 'Main instructor') {
    roleClass = 'main';
  } else {
    const assignment = lessonAssignments.find((la) => Number(la.instructor_id) === item.id);
    const role = assignment?.instructor_role ?? '';
    if (role === 'instructor') roleClass = 'additional';
    else if (role === 'substitute' || role === 'sub') roleClass = 'sub';
    else roleClass = role || 'additional';
  }

  // Match calendar: dashed outline when this instructor still has an open sub request.
  if (hasPendingSubRequest(lessonAssignments, item.id)) {
    return `${roleClass} sub-requested`;
  }
  return roleClass;
}

function namesForLessonRole(assignments: LessonAssignment[], role: string): string[] {
  return assignments
    .filter((la) => la.instructor_role === role && la.instructor_id != null && la.instructor)
    .map((la) => formatInstructorName(la.instructor))
    .filter(Boolean);
}

/** Main, substitute, or additional instructor on this lesson (not trainee/observer). */
export function viewerIsLeadInstructorOnLesson(
  lesson: Pick<Lesson, 'lesson_assignments'>,
  course: Pick<Course, 'course_assignments'>,
  viewerInstructorId: number
): boolean {
  const viewerId = Number(viewerInstructorId);
  const assignments = lesson.lesson_assignments ?? [];

  const isMain = (course.course_assignments ?? []).some(
    (ca) =>
      ca.instructor_id != null &&
      Number(ca.instructor_id) === viewerId &&
      !hasFilledSubRequest(assignments, ca.instructor_id)
  );
  if (isMain) return true;

  const viewerAssignment = assignments.find((la) => Number(la.instructor_id) === viewerId);
  if (!viewerAssignment) return false;

  const role = viewerAssignment.instructor_role;
  return role === 'substitute' || role === 'sub' || role === 'instructor';
}

/** Short lines for calendar cards, e.g. "Trainee: Jane Doe". */
export function getLessonTraineeObserverNoteLines(
  lesson: Pick<Lesson, 'lesson_assignments'>,
  course: Pick<Course, 'course_assignments'>,
  viewerInstructorId: number | null | undefined
): string[] {
  if (viewerInstructorId == null) return [];
  if (!viewerIsLeadInstructorOnLesson(lesson, course, viewerInstructorId)) return [];

  const assignments = lesson.lesson_assignments ?? [];
  const lines: string[] = [];
  const traineeNames = namesForLessonRole(assignments, 'trainee');
  const observerNames = namesForLessonRole(assignments, 'observer');

  if (traineeNames.length === 1) lines.push(`Trainee: ${traineeNames[0]}`);
  else if (traineeNames.length > 1) lines.push(`Trainees: ${traineeNames.join(', ')}`);

  if (observerNames.length === 1) lines.push(`Observer: ${observerNames[0]}`);
  else if (observerNames.length > 1) lines.push(`Observers: ${observerNames.join(', ')}`);

  return lines;
}
